import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

// Get 45 days range (from 45 days ago to today)
const get45DaysRange = () => {
  const endDate = dayjs().endOf('day');
  const startDate = dayjs().subtract(45, 'days').startOf('day');
  return [startDate, endDate];
};

// Initial state
const initialState = {
  orders: [],
  orderItems: [],
  filteredOrderItems: [],
  selectedOrder: null,
  loading: false,
  kpiLoading: false,
  error: null,
  kpiMetrics: null,
  pagination: {
    current: 1,
    pageSize: 30,
    total: 0
  },
  filters: {
    searchText: '',
    selectedStatus: [],
    selectedDateRange: get45DaysRange(),
    selectedSellerAccount: 'all',
    selectedCustomizationStatus: 'all',
    selectedPrinter: 'all',
    selectedFulfillmentStatus: 'all',
    selectedFulfillmentChannel: 'all',
    selectedShipmentStatus: 'all',
    selectedShipmentDetail: 'all',
    selectedSupporter: 'all',
    selectedCategory: 'all',
    selectedMessageStatus: 'all',
    selectedShipDateDue: 'all',
    selectedDeliveryDateDue: 'all',
    selectedConfirmShipment: 'all',
    selectedUpdatedLastMile: 'all',
    selectedMockupSentAt: 'all',
    selectedTitle: '',
    selectedSellerSku: '',
    selectedEmployeeId: '',
    selectedExcludeTitle: '',
    selectedExcludeSellerSku: '',
    selectedBasecost: 'all',
    selectedQuantity: null,
  },
};

// Orders slice
const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setOrders: (state, action) => {
      state.orders = action.payload;
    },
    setOrderItems: (state, action) => {
      state.orderItems = action.payload;
      state.filteredOrderItems = action.payload;
    },
    setFilteredOrderItems: (state, action) => {
      state.filteredOrderItems = action.payload;
    },
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setKpiLoading: (state, action) => {
      state.kpiLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setKpiMetrics: (state, action) => {
      state.kpiMetrics = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        searchText: '',
        selectedStatus: [],
        selectedDateRange: null,
        selectedSellerAccount: 'all',
        selectedCustomizationStatus: 'all',
        selectedPrinter: 'all',
        selectedFulfillmentStatus: 'all',
        selectedFulfillmentChannel: 'all',
        selectedShipmentStatus: 'all',
        selectedShipmentDetail: 'all',
        selectedSupporter: 'all',
        selectedCategory: 'all',
        selectedMessageStatus: 'all',
        selectedShipDateDue: 'all',
        selectedDeliveryDateDue: 'all',
        selectedConfirmShipment: 'all',
        selectedUpdatedLastMile: 'all',
        selectedMockupSentAt: 'all',
        selectedTitle: '',
        selectedSellerSku: '',
        selectedEmployeeId: '',
        selectedExcludeTitle: '',
        selectedExcludeSellerSku: '',
        selectedBasecost: 'all',
        selectedQuantity: null,
      };
    },
    updateOrderItem: (state, action) => {
      const { id, field, value } = action.payload;
      
      // Update in orderItems array
      const orderItemIndex = state.orderItems.findIndex(item => item.id === id);
      if (orderItemIndex !== -1) {
        state.orderItems[orderItemIndex][field] = value;
      }
      
      // Update in filteredOrderItems array
      const filteredItemIndex = state.filteredOrderItems.findIndex(item => item.id === id);
      if (filteredItemIndex !== -1) {
        state.filteredOrderItems[filteredItemIndex][field] = value;
      }
    },
    replaceOrderItem: (state, action) => {
      const updatedItem = action.payload;

      const orderItemIndex = state.orderItems.findIndex(item => item.id === updatedItem.id);
      if (orderItemIndex !== -1) {
        state.orderItems[orderItemIndex] = updatedItem;
      }

      const filteredItemIndex = state.filteredOrderItems.findIndex(item => item.id === updatedItem.id);
      if (filteredItemIndex !== -1) {
        state.filteredOrderItems[filteredItemIndex] = updatedItem;
      }
    },
  },
});

export const {
  setOrders,
  setOrderItems,
  setFilteredOrderItems,
  setSelectedOrder,
  setLoading,
  setKpiLoading,
  setError,
  setKpiMetrics,
  setPagination,
  setFilters,
  clearFilters,
  updateOrderItem,
  replaceOrderItem,
} = ordersSlice.actions;

export default ordersSlice.reducer;
